<template>
  <component
    :is="actualTag"
    class="inline-flex items-center border border-solid rounded-[100px] cursor-pointer transition-colors duration-200 no-underline box-border leading-snug"
    :class="[
      variantClasses,
      sizeClasses,
      layoutClasses,
      block ? 'flex w-full' : '',
    ]"
    :disabled="actualTag === 'button' ? disabled : undefined"
    :href="actualTag === 'a' ? href : undefined"
    :type="actualTag === 'button' ? actualType : undefined"
    :data-variant="actualVariant"
    :data-size="actualSize"
    :data-icon-position="iconPosition || undefined"
    @click="handleClick"
  >
    <span
      v-if="hasIcon && iconPosition !== 'right'"
      data-testid="icon-wrapper"
      class="inline-flex items-center justify-center shrink-0 mr-1"
      :class="iconSizeClasses"
    >
      <slot name="icon" />
    </span>
    <span :class="labelClasses">
      <slot />
    </span>
    <span
      v-if="hasIcon && iconPosition === 'right'"
      data-testid="icon-wrapper"
      class="inline-flex items-center justify-center shrink-0 ml-1"
      :class="iconSizeClasses"
    >
      <slot name="icon" />
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue';

type Variant = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'cta';
type Size = 'medium' | 'small' | 'extrasmall';
type IconPosition = 'front' | 'left' | 'right';
type Tag = 'button' | 'a';
type ButtonType = 'button' | 'submit' | 'reset';

const props = defineProps<{
  variant?: Variant;
  size?: Size;
  iconPosition?: IconPosition;
  disabled?: boolean;
  block?: boolean;
  tag?: Tag;
  type?: ButtonType;
  href?: string;
}>();

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void;
}>();

const slots = useSlots();

const actualVariant = computed(() => props.variant ?? 'primary');
const actualSize = computed(() => props.size ?? 'medium');
const actualTag = computed(() => props.tag ?? 'button');
const actualType = computed(() => props.type ?? 'button');
const hasIcon = computed(() => !!slots.icon);

// variant × state（hover は CSS :hover、disabled は prop）
const variantClasses = computed(() => {
  if (props.disabled) {
    const disabledMap: Record<Variant, string> = {
      primary: 'bg-primary border-primary text-white font-bold opacity-[0.16]',
      secondary:
        'bg-primary border-primary text-white font-normal opacity-[0.16]',
      tertiary: 'bg-white border-icon-muted text-ink font-normal opacity-50',
      danger: 'bg-danger border-danger text-white font-bold opacity-[0.16]',
      // Figma node 3125:35330 — 進行CTA disabled
      cta:
        'bg-btn-cta-disabled border-btn-cta-disabled text-fig-gray-500 font-bold',
    };
    return `${disabledMap[actualVariant.value]} cursor-not-allowed`;
  }
  const map: Record<Variant, string> = {
    primary:
      'bg-primary border-primary text-white font-bold hover:bg-primary-hover hover:border-primary-hover',
    secondary:
      'bg-white border-primary text-primary font-normal hover:bg-btn-secondary-hover',
    tertiary:
      'bg-white border-btn-tertiary-border text-primary font-normal hover:bg-btn-secondary-hover hover:border-primary',
    danger:
      'bg-danger border-danger text-white font-bold hover:bg-danger-hover hover:border-danger-hover',
    // Figma node 3125:35314 — 進行CTA normal。Figma に枠線は無いが、
    // ベースクラスが border border-solid を常に当てるため背景と同色で打ち消す。
    cta:
      'bg-btn-cta border-btn-cta text-ink font-bold hover:bg-btn-cta-hover hover:border-btn-cta-hover',
  };
  return map[actualVariant.value];
});

// 高さ・フォントサイズ
const sizeClasses = computed(() => {
  const map: Record<Size, string> = {
    medium: 'h-12 text-sm',
    small: 'h-11 text-sm',
    extrasmall: 'h-8 text-xs',
  };
  return map[actualSize.value];
});

// アイコン配置による padding と中央寄せ（front はアイコン+テキストを中央寄せ）
const layoutClasses = computed(() => {
  const isFront = props.iconPosition === 'front' || !hasIcon.value;
  if (actualSize.value === 'extrasmall') {
    return isFront ? 'justify-center pl-3 pr-4' : 'px-3';
  }
  return isFront ? 'justify-center pl-4 pr-6' : 'px-4';
});

// ラベル: left/right はテキストを伸長して両端揃え、front/none は中央
const labelClasses = computed(() => {
  if (
    hasIcon.value &&
    (props.iconPosition === 'left' || props.iconPosition === 'right')
  ) {
    return 'flex-1 text-center';
  }
  return 'text-center';
});

const iconSizeClasses = computed(() =>
  actualSize.value === 'extrasmall' ? 'text-xs' : 'text-sm'
);

function handleClick(event: MouseEvent) {
  if (props.disabled) {
    event.preventDefault();
    return;
  }
  emit('click', event);
}
</script>
